import type { ChangeEvent, KeyboardEvent } from "react"
import { useCallback, useState } from "react"
import {
	IconEnvelopeOutline18 as MailIcon,
	IconXmarkOutline18 as XIcon,
} from "nucleo-ui-outline-18"
import Balancer from "react-wrap-balancer"

import { MembershipRole } from "@productlane/db/browser"
import { cn } from "@productlane/lib/src/browser"
import { Avatar, Button, IconButton, Input } from "@productlane/ui"

import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import {
	deriveName,
	EMAIL_SEPARATORS,
	invalidEmailMessage,
	mergeEmails,
	partitionEmails,
	splitEmails,
} from "@/lib/invite-emails"
import { showInviteErrorToast } from "@/lib/invite-error"
import { useSession } from "@/lib/session"

export function InviteSlide({ nextSlide }: { nextSlide: () => void }) {
	const context = api.useUtils()

	const { session } = useSession()

	const inviteMutation = api.workspace.inviteMembers.useMutation()

	const [invitees, setInvitees] = useState<string[]>([])
	const [draft, setDraft] = useState("")
	const [error, setError] = useState<string | null>(null)

	// Returns the merged list so callers can send it without waiting for a re-render.
	const commitDraft = useCallback((): string[] | null => {
		const { valid, invalid } = partitionEmails(splitEmails(draft))
		if (invalid.length > 0) {
			setError(invalidEmailMessage(invalid))
			return null
		}
		const merged = mergeEmails(invitees, valid)
		setInvitees(merged)
		setDraft("")
		setError(null)
		return merged
	}, [draft, invitees])

	const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value
		if (!EMAIL_SEPARATORS.test(value)) {
			setDraft(value)
			setError(null)
			return
		}

		const entries = splitEmails(value)
		const trailing = EMAIL_SEPARATORS.test(value.slice(-1))
			? ""
			: (entries.pop() ?? "")
		const { valid, invalid } = partitionEmails(entries)

		setInvitees((current) => mergeEmails(current, valid))
		setDraft([...invalid, trailing].filter(Boolean).join(", "))
		setError(invalid.length > 0 ? invalidEmailMessage(invalid) : null)
	}, [])

	const handleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLInputElement>) => {
			if (event.key !== "Enter") return
			event.preventDefault()
			if (!draft.trim()) {
				setError("Enter an email address.")
				return
			}
			commitDraft()
		},
		[commitDraft, draft],
	)

	const removeInvitee = useCallback((email: string) => {
		setInvitees((current) => current.filter((entry) => entry !== email))
	}, [])

	const submit = useCallback(async () => {
		const emails = commitDraft()
		if (!emails) return

		if (emails.length === 0) {
			setError("Add at least one email address.")
			return
		}

		let results
		try {
			results = await inviteMutation.mutateAsync({
				invites: emails.map((email) => ({
					email,
					name: deriveName(email),
				})),
				role: MembershipRole.USER,
			})
		} catch (error) {
			showInviteErrorToast(error as { message?: string })
			return
		}
		const sent = results.filter((r) => r.ok).length
		const failures = results.filter((r) => !r.ok)

		await context.workspace.members.invalidate()

		if (failures.length > 0) {
			toast.error({
				description: failures
					.map((f) => `${f.email}: ${f.error ?? "Failed to invite"}`)
					.join("\n"),
				title: `Could not invite ${failures.length} ${failures.length > 1 ? "people" : "person"}`,
			})
		}

		if (sent > 0) {
			toast.success({
				description: `Successfully sent ${sent} invitation${sent > 1 ? "s" : ""}!`,
				title: "Members Invited",
			})
			analytics.track("setup-workspace-user-invited", {
				userId: session?.userId,
				workspaceId: session?.workspaceId,
			})
			nextSlide()
		}
	}, [commitDraft, inviteMutation, context, session, nextSlide])

	return (
		<div className="flex flex-col items-center text-center">
			<div className="mb-1 text-3xl font-bold tracking-tight">Invite team</div>
			<Balancer className="tracking-tight text-secondary">
				Support your customers together.
			</Balancer>

			<div className="mt-8 w-full max-w-lg text-left">
				<div className="flex items-start gap-2">
					<Input
						name="invite-email"
						className="flex-1"
						icon={MailIcon}
						inputClassName="h-10 rounded-lg"
						placeholder="jane@acme.com"
						type="email"
						autoComplete="off"
						value={draft}
						errorMessage={error}
						onChange={handleChange}
						onKeyDown={handleKeyDown}
						autoFocus
					/>
					<Button
						variant="outline"
						size="lg"
						className="h-10 flex-none rounded-lg"
						onClick={() => {
							if (!draft.trim()) {
								setError("Enter an email address.")
								return
							}
							commitDraft()
						}}
					>
						Add
					</Button>
				</div>

				{invitees.length === 0 ? (
					<div className="mt-3 flex h-28 flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
						<div className="flex">
							{[0, 1, 2].map((index) => (
								<div
									key={index}
									className={cn(
										"h-8 w-8 flex-none rounded-full border border-gray-200 bg-gray-100 ring-4 ring-white dark:border-gray-700 dark:bg-gray-750 dark:ring-gray-850",
										index > 0 && "-ml-2",
									)}
								/>
							))}
						</div>
						<p className="text-[13px] text-tertiary">
							Everyone you add shows up here.
						</p>
					</div>
				) : (
					<div className="mt-3 max-h-56 space-y-2 overflow-y-auto pb-2">
						{invitees.map((email) => (
							<div
								key={email}
								className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700/80"
							>
								<Avatar
									size="sm"
									name={deriveName(email)}
									colorSeed={email}
									image={null}
								/>
								<div className="min-w-0 flex-1">
									<div className="truncate text-sm font-medium capitalize">
										{deriveName(email)}
									</div>
									<div className="truncate text-xs text-tertiary">{email}</div>
								</div>
								<IconButton
									variant="ghost"
									size="md"
									Icon={XIcon}
									aria-label={`Remove ${email}`}
									onClick={() => removeInvitee(email)}
								/>
							</div>
						))}
					</div>
				)}
			</div>

			<Button
				size="xl"
				className="mt-8 w-full max-w-xxs"
				onClick={submit}
				isLoading={inviteMutation.isLoading}
			>
				{invitees.length > 0
					? `Send ${invitees.length} invite${invitees.length > 1 ? "s" : ""}`
					: "Send invites"}
			</Button>
			<Button
				variant="link"
				className="mt-1 w-full max-w-xxs opacity-70 hover:opacity-80"
				onClick={() => {
					analytics.track("skip-user-invite-onboarding")
					nextSlide()
				}}
			>
				{"I'll do this later"}
			</Button>
		</div>
	)
}
